export const IT_PLANS = [
  {
    slug: "assist",
    name: "Silvertree Assist",
    short: "Assist",
    headline: "Reliable IT help when something breaks.",
    price: "$495",
    per: "per month",
    included: "Includes support for up to 5 users",
    extra: "+$99/month per additional user",
    message:
      "Reliable IT support when your team needs it. Designed for small businesses that want a dependable technology team to call without purchasing a full managed IT service.",
    cta: "Get Reliable IT Support",
    formCta: "Get Assist",
    defaultNeed: "Day-to-day IT support",
    inclusions: [
      "Remote business-hours help desk (M–F, 8am–5pm CT)",
      "Microsoft 365 end-user support",
      "Basic device monitoring",
      "30-minute response target for qualifying urgent requests",
      "Easy to understand answers - no translation required",
      "No long-term managed-IT commitment required",
    ],
    forWho:
      "Small firms that want a dependable technology team on call — without outsourcing everything.",
  },
  {
    slug: "managed",
    name: "Silvertree Managed",
    short: "Managed",
    headline: "Stop managing IT yourself.",
    price: "$745",
    per: "per month",
    included: "Includes support for up to 5 users",
    extra: "+$149/month per additional user",
    popular: true,
    message:
      "Your outsourced IT department. Proactive IT support, device management, patching, monitoring, Microsoft 365 administration, endpoint protection, and routine technology management.",
    cta: "Schedule a Managed IT Review",
    formCta: "Schedule My Managed IT Review",
    defaultNeed: "Day-to-day IT support",
    inclusions: [
      "Everything in Assist",
      "Proactive device monitoring & patch management",
      "Microsoft 365 administration",
      "Managed endpoint security",
      "Backup monitoring*",
      "Basic security reporting",
      "Regular technology review",
    ],
    forWho:
      "Businesses ready to hand off day-to-day IT — support, devices, patching, and administration — to one accountable team.",
  },
  {
    slug: "secure",
    name: "Silvertree Secure",
    short: "Secure",
    headline: "Managed IT with security watching around the clock.",
    price: "$995",
    per: "per month",
    included: "Includes support for up to 5 users",
    extra: "+$199/month per additional user",
    message:
      "Managed IT with security watching around the clock. Everything in Managed plus enhanced endpoint security, identity protection, security reporting, and 24/7 managed security monitoring.",
    cta: "Schedule a Security & IT Review",
    formCta: "Schedule My Security & IT Review",
    defaultNeed: "Cybersecurity",
    inclusions: [
      "Everything in Managed",
      "24/7 managed security monitoring",
      "Identity protection",
      "Enhanced endpoint protection",
      "Enhanced security reporting",
      "Regular technology & security review",
    ],
    forWho:
      "Security-conscious businesses — and any company handling sensitive client, financial, or regulated data.",
  },
];

export const getPlan = (slug) => IT_PLANS.find((p) => p.slug === slug);

export const PLAN_COMPARISON = [
  { capability: "Remote business-hours help desk", assist: "Included", managed: "Included", secure: "Included" },
  { capability: "Microsoft 365 end-user support", assist: "Included", managed: "Included", secure: "Included" },
  { capability: "Device monitoring", assist: "Basic", managed: "Included", secure: "Included" },
  { capability: "Patch management", assist: "—", managed: "Included", secure: "Included" },
  { capability: "Microsoft 365 administration", assist: "—", managed: "Included", secure: "Included" },
  { capability: "Managed endpoint security", assist: "—", managed: "Included", secure: "Included" },
  { capability: "Backup monitoring*", assist: "—", managed: "Included", secure: "Included" },
  { capability: "24/7 managed security monitoring", assist: "—", managed: "—", secure: "Included" },
  { capability: "Identity protection", assist: "—", managed: "Optional", secure: "Included" },
  { capability: "Security reporting", assist: "—", managed: "Basic", secure: "Enhanced" },
  { capability: "Technology review", assist: "—", managed: "Included", secure: "Included" },
];

export const PRICING_DISCLOSURE =
  "Microsoft 365 licensing, backup services, hardware, projects, onsite service, and certain third-party security products may be billed separately depending on your environment.";

export const BACKUP_FOOTNOTE =
  "* Backup services, storage, and licenses may be billed separately depending on the protected systems and recovery requirements.";

export const SNAPSHOT_CREDIT_OFFER =
  "Apply your $495 one-time Technology Health Snapshot toward onboarding when you enroll in Silvertree Managed or Secure within 30 days.";

export const IT_PROBLEMS = [
  {
    title: "Nobody really owns IT",
    body: "The office manager resets passwords, someone's nephew set up the Wi-Fi, and every problem lands on whoever is least busy. Nobody is actually responsible.",
  },
  {
    title: "The same problems keep coming back",
    body: "The printer, the VPN, that one computer that freezes every Tuesday. Recurring outages get patched, never fixed — and they cost you hours every month.",
  },
  {
    title: "Support takes days, not minutes",
    body: "You submit a ticket and wait. Meanwhile a team member can't work, a customer is waiting, and you're paying payroll for people staring at error messages.",
  },
  {
    title: "You're not sure how protected you actually are",
    body: "Backups probably run. Antivirus is probably on. Probably isn't a security strategy — and you'd rather find out now than after an incident.",
  },
  {
    title: "New hires wait days for a working computer",
    body: "A new employee shows up and spends their first week without email, the right software, or access to the systems they were hired to use.",
  },
  {
    title: "Every vendor points at another vendor",
    body: "The internet company blames the phone system, the software vendor blames the network — and you're stuck in the middle making the calls.",
  },
];

export const HOW_IT_WORKS = [
  {
    step: "Schedule a 15-minute technology check",
    body: "A short, no-pressure conversation about your business, your current setup, and what's been frustrating you.",
  },
  {
    step: "Choose the plan that fits",
    body: "Assist, Managed, or Secure — with visible pricing, clear inclusions, and no surprise line items.",
  },
  {
    step: "We onboard your business",
    body: "We document your environment, secure your accounts, and set up monitoring — without disrupting your team's workday.",
  },
  {
    step: "Contact Silvertree whenever you need help",
    body: "Call, email, or submit a request. Qualifying urgent business-hours requests receive a human response within 30 minutes.",
  },
];

export const SECURITY_ITEMS = [
  {
    title: "Multi-factor authentication",
    body: "A second check at sign-in, so a stolen password alone can't open your email or files.",
  },
  {
    title: "Managed device security",
    body: "Every company computer protected and watched for suspicious behavior — not just old-fashioned antivirus.",
  },
  {
    title: "Backups that actually restore",
    body: "Your critical data copied safely and checked, so a bad day doesn't become a lost month.",
  },
  {
    title: "Patching & updates",
    body: "Known security holes closed on a schedule — quietly, without interrupting your team.",
  },
  {
    title: "Monitoring",
    body: "We watch the health of your systems so small warning signs get handled before they become outages.",
  },
  {
    title: "24/7 security option",
    body: "With Silvertree Secure, trained eyes watch for threats around the clock — including nights, weekends, and holidays.",
  },
];

export const IT_FAQS = [
  {
    q: "How does plan pricing work?",
    a: "Each plan is a fixed monthly package that includes support for up to 5 users — Assist $495/month, Managed $745/month, Secure $995/month. Only users beyond five add a per-user charge ($99, $149, or $199 per additional user respectively). A 5-user team pays just the base price; an 8-user Assist team pays $495 + 3 × $99 = $792/month.",
  },
  {
    q: "Do you charge a setup or onboarding fee?",
    a: "Onboarding is quoted based on the complexity of your environment. Silvertree may waive onboarding for qualifying annual Managed or Secure agreements.",
  },
  {
    q: "Is onsite support included?",
    a: "Our plans are designed to be remote-first — most issues are resolved faster remotely. Onsite support is available when needed and may be billed separately unless your agreement states otherwise.",
  },
  {
    q: "Do I have to sign a long-term contract?",
    a: "We'll walk through term options during your technology check. We earn renewals with service, not lock-in — and we'll never make leaving harder than it should be.",
  },
  {
    q: "What does the 30-minute response promise actually mean?",
    a: "Response means a real person engages with your request — not an auto-reply. Qualifying urgent requests during business hours (M–F, 8am–5pm CT) receive a human response within 30 minutes. Resolution time depends on the issue; we'll always tell you what we're doing and when to expect the fix.",
  },
  {
    q: "Do you provide after-hours support?",
    a: "24/7 managed security monitoring is included with Secure. General after-hours IT support may be available separately for qualifying customers.",
  },
  {
    q: "Are Microsoft 365 licenses included?",
    a: "Microsoft 365 licensing is available through Silvertree and is billed separately unless specifically included in your agreement.",
  },
  {
    q: "Is backup included?",
    a: "Backup monitoring is included in qualifying Managed and Secure environments when Silvertree manages the backup platform. Backup licensing, storage, and recovery requirements are priced based on your environment.",
  },
  {
    q: "Can you work with our existing vendors and software?",
    a: "Yes. We coordinate with your line-of-business software vendors, internet provider, and phone system — so you're no longer the go-between when something breaks.",
  },
  {
    q: "We already have an IT provider. How hard is it to switch?",
    a: "Easier than you'd expect — and we handle the awkward parts. We quietly document your environment, capture credentials, plan the cutover, and coordinate the handoff. See how a transition works on our switching page, or ask during your technology check.",
  },
  {
    q: "Can I buy just a one-time assessment?",
    a: "Yes. The Technology Health Snapshot starts at $495 and can be purchased without signing up for recurring support.",
  },
  {
    q: "Can the Snapshot fee be applied toward support?",
    a: "Yes. The $495 Snapshot may be applied toward qualifying Managed or Secure onboarding when enrollment occurs within 30 days.",
  },
];

export const FORM_OPTIONS = {
  employees: ["1–5", "6–10", "11–25", "26–50", "51–100", "100+"],
  computers: ["1–5", "6–10", "11–25", "26–50", "51–100", "100+"],
  currentIt: [
    "Internal staff handles IT",
    "We use another MSP / IT company",
    "An independent consultant",
    "Nobody officially owns IT",
    "Other",
  ],
  primaryNeed: [
    "Day-to-day IT support",
    "Cybersecurity",
    "Microsoft 365",
    "Backup & recovery",
    "Network & Wi-Fi",
    "Technology assessment",
    "Switching IT providers",
  ],
  urgency: ["We need help now", "Within 30 days", "Within 90 days", "Just researching"],
};

export const IT_SUPPORT_INDUSTRIES = {
  "professional-services": {
    heading: "IT support for accounting & professional services firms",
    body: "Deadline seasons don't wait for a slow help desk. We keep your practice management, tax, and document systems running, your client data protected, and your staff productive when it matters most.",
    points: [
      "Priority responsiveness during busy season — when downtime costs billable hours",
      "Secure client document handling and access controls",
      "Microsoft 365 managed so email, calendars, and files just work",
    ],
  },
  "legal-services": {
    heading: "IT support for law firms",
    body: "Confidentiality isn't optional. We support your matter, document, and email systems with security controls appropriate for privileged information — and we respond fast, because a firm can't bill hours on a frozen workstation.",
    points: [
      "Access controls and MFA around client and matter data",
      "Support for document management and legal-specific software vendors",
      "Fast onboarding and offboarding as associates and staff change",
    ],
  },
  "construction-field-services": {
    heading: "IT support for construction & field services",
    body: "Your team isn't at a desk — it's on job sites with tablets, phones, and hotspot connections. We keep field devices, office systems, and project software connected so paperwork doesn't stall the job.",
    points: [
      "Field device setup, support, and replacement without slowing crews",
      "Office-to-field connectivity: shared files, plans, and project systems",
      "Vendor coordination for internet, phones, and job-site connectivity",
    ],
  },
  nonprofits: {
    heading: "IT support for nonprofits",
    body: "Every dollar is scrutinized, and technology has to earn its place in your budget. We provide right-sized support, help you claim nonprofit software discounts, and protect donor data without enterprise price tags.",
    points: [
      "Right-sized plans and guidance on nonprofit licensing discounts",
      "Donor and program data protected with practical security controls",
      "Board-ready clarity about where technology money goes",
    ],
  },
  "financial-services-insurance": {
    heading: "IT support for financial services & insurance",
    body: "Regulators, carriers, and clients all expect your systems to be secure and documented. We deliver responsive support with the security posture and paper trail your industry demands.",
    points: [
      "Security controls and reporting that support audits and carrier requirements",
      "Managed patching and monitoring across every workstation",
      "24/7 security monitoring available with Silvertree Secure",
    ],
  },
};
